/**
 * BAM IQ in the platform (milestone 2, D108; UX proposal §D.2, §E.3; the
 * orb, D149):
 *
 *   BamIQProvider   one shared conversation state, mounted above the page so
 *                   it survives moving between pages; ⌘J / Ctrl+J
 *   BamIQOrb        the one way in: an orb in the bottom-right corner that
 *                   opens into a small ask bar. Replaces the top-bar button
 *                   and the old Ask BAM bubble for anyone BAM IQ is open to
 *   BamIQPanel      the conversation over any page: on a wide screen it pushes
 *                   the page aside (drag its edge to resize), so a source it
 *                   opens sits beside the conversation; a full-screen sheet on
 *                   a phone
 *   BamIQWorkspace  the #/iq page: conversations (pinned, by day, searchable),
 *                   what BAM IQ knows about the coach, and the conversation
 *
 * Pages say what's open (useBamIQPage: a drill, a masterclass, a research
 * paper, a player's plan) so the coach can attach it with a tap (D122).
 * Nothing is attached automatically.
 *
 * Off unless bamiqAvailable() in featureFlags.js says so (the listed account,
 * web only, never the iPhone app until a reviewed build turns it on: D106,
 * D145), and the server checks again.
 */
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { X, Plus, Maximize2, History, UserRound, ArrowUp, CornerDownLeft } from "lucide-react";
import { GOLD } from "../constants/theme";
import { BAM_LOGO_PNG } from "../constants/branding";
import { useBamIQ } from "./useBamIQ";
import BamIQConversation from "./BamIQConversation";
import { ThreadList, MemoryCard } from "./BamIQSide";
import BamIQCases from "./BamIQCases";

const ff = "'DM Sans', sans-serif";
const Ctx = createContext(null);
export const useBamIQContext = () => useContext(Ctx);

export const ORB_SUGGESTIONS = [
  "My guards panic against full-court pressure",
  "What's a drag screen?",
  "A warm-up that isn't just lines",
  "How do I teach reading the help defender?",
  "My team looks great in drills but lost in games",
  "Constraints for better spacing in 3v3",
];

const PAGE_THREADS_KEY = "bamiq_page_threads";   // which conversation a page was last attached to, on this device
const PANEL_W_KEY = "bamiq_panel_w";
export const PANEL_MIN = 380;
export const PANEL_MAX = 720;
const PUSH_MIN_PAGE = 720;   // narrower than this beside the panel, the panel covers the page instead

const store = {
  get(k, d) { try { const v = localStorage.getItem(k); return v == null ? d : JSON.parse(v); } catch { return d; } },
  set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* private window */ } },
};

const BAMIQ_CSS = `
.bamiq-dots::after { content: ""; display: inline-block; width: 1.2em; text-align: left; animation: bamiq-dots 1.2s steps(4, end) infinite; }
@keyframes bamiq-dots { 0% { content: ""; } 25% { content: "."; } 50% { content: ".."; } 75% { content: "..."; } }
@property --bamiq-a { syntax: "<angle>"; inherits: false; initial-value: 0deg; }
@keyframes bamiq-turn { to { --bamiq-a: 360deg; } }
@keyframes bamiq-breathe { 0%, 100% { box-shadow: 0 0 0 0 rgba(226,221,159,0), 0 12px 32px rgba(0,0,0,.45); } 50% { box-shadow: 0 0 0 7px rgba(226,221,159,.10), 0 12px 32px rgba(0,0,0,.45); } }
@keyframes bamiq-blink { 50% { opacity: 0; } }
@keyframes bamiq-pulse { 0%, 100% { transform: scale(.75); opacity: .55; } 50% { transform: scale(1); opacity: 1; } }
@keyframes bamiq-sheen { to { background-position: -200% 0; } }
@keyframes bamiq-pop { from { transform: scale(.85); opacity: 0; } to { transform: none; opacity: 1; } }
@keyframes bamiq-in { from { transform: translateX(28px); opacity: 0; } to { transform: none; opacity: 1; } }
@keyframes bamiq-up { from { transform: translateY(6px); opacity: 0; } to { transform: none; opacity: 1; } }
@keyframes bamiq-rec { 0% { box-shadow: 0 0 0 0 rgba(229,115,115,.55); } 100% { box-shadow: 0 0 0 10px rgba(229,115,115,0); } }
.bamiq-ring { position: relative; }
.bamiq-ring::before { content: ""; position: absolute; inset: 0; border-radius: inherit; padding: 1.5px; pointer-events: none;
  background: conic-gradient(from var(--bamiq-a), #E2DD9F, rgba(226,221,159,.12) 30%, #E2DD9F 55%, rgba(226,221,159,.18) 80%, #E2DD9F);
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0); -webkit-mask-composite: xor; mask-composite: exclude;
  animation: bamiq-turn 7s linear infinite; }
.bamiq-orb { animation: bamiq-breathe 4.5s ease-in-out infinite; transition: width .34s cubic-bezier(.2,.9,.25,1.12), transform .18s ease, background .2s ease; }
.bamiq-orb:hover { transform: translateY(-1px); }
.bamiq-orb-wrap { position: fixed; right: max(20px, env(safe-area-inset-right, 0px)); bottom: max(20px, env(safe-area-inset-bottom, 0px)); z-index: 5100; }
body.bam-has-bottom-nav .bamiq-orb-wrap { bottom: calc(58px + env(safe-area-inset-bottom, 0px) + 14px); }
.bamiq-caret { display: inline-block; width: 1.5px; height: 1.1em; margin-left: 1px; background: #E2DD9F; vertical-align: -3px; animation: bamiq-blink 1s steps(1) infinite; }
.bamiq-stream > :last-child::after { content: ""; display: inline-block; width: 7px; height: 7px; margin-left: 6px; border-radius: 50%; background: #E2DD9F; vertical-align: 1px; animation: bamiq-pulse 1s ease-in-out infinite; }
.bamiq-shimmer { background: linear-gradient(90deg, var(--bamiq-dim) 0%, var(--bamiq-dim) 35%, #E2DD9F 50%, var(--bamiq-dim) 65%, var(--bamiq-dim) 100%) 0 0 / 200% 100%;
  -webkit-background-clip: text; background-clip: text; color: transparent; animation: bamiq-sheen 2.4s linear infinite; }
.bamiq-orb-mini { width: 9px; height: 9px; border-radius: 50%; background: #E2DD9F; animation: bamiq-pulse 1.1s ease-in-out infinite; flex-shrink: 0; }
.bamiq-pop { animation: bamiq-pop .32s cubic-bezier(.2,.9,.3,1.3) both; }
.bamiq-panel { animation: bamiq-in .26s cubic-bezier(.2,.8,.2,1); }
.bamiq-rise { animation: bamiq-up .22s ease-out both; }
.bamiq-listening { animation: bamiq-rec 1.2s ease-out infinite; }
.bamiq-resize:hover > span, .bamiq-resize.dragging > span { opacity: 1 !important; }
.bamiq-thread-row .bamiq-row-tools { opacity: .35; transition: opacity .12s ease; }
.bamiq-thread-row:hover .bamiq-row-tools, .bamiq-thread-row:focus-within .bamiq-row-tools { opacity: 1; }
@media (prefers-reduced-motion: reduce) {
  .bamiq-dots::after { animation: none; content: "..."; }
  .bamiq-ring::before, .bamiq-orb, .bamiq-caret, .bamiq-stream > :last-child::after, .bamiq-shimmer, .bamiq-orb-mini, .bamiq-pop, .bamiq-panel, .bamiq-rise, .bamiq-listening { animation: none !important; }
  .bamiq-shimmer { color: var(--bamiq-dim); background: none; }
  .bamiq-orb { transition: none; }
}
`;

export function BamIQProvider({ enabled, children }) {
  const iq = useBamIQ({ enabled });
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(null);   // what's open on the current page, offered for attaching
  const [orbOpen, setOrbOpen] = useState(false);
  // Remember which conversation a page was attached to, so coming back to it offers that conversation.
  const { threadId, messages } = iq;
  useEffect(() => {
    if (!enabled || !threadId) return;
    const items = messages.filter(m => m.role === "coach").flatMap(m => m.attachments || []).filter(a => a.type !== "player" && a.id);
    if (!items.length) return;
    const map = store.get(PAGE_THREADS_KEY, {});
    items.forEach(a => { map[`${a.type}:${a.id}`] = threadId; });
    store.set(PAGE_THREADS_KEY, map);
  }, [enabled, threadId, messages]);
  const pageThread = useMemo(() => {
    if (!page || page.type === "player") return null;
    const id = store.get(PAGE_THREADS_KEY, {})[`${page.type}:${page.id}`];
    return id && id !== threadId ? iq.threads.find(t => t.id === id) || null : null;
  }, [page, threadId, iq.threads]);
  return (
    <Ctx.Provider value={{ iq, enabled, open, setOpen, page, setPage, orbOpen, setOrbOpen, pageThread }}>
      {enabled && <style>{BAMIQ_CSS}</style>}
      {children}
    </Ctx.Provider>
  );
}

/**
 * A page says what's open: { type: "drill" | "masterclass" | "research" | "player", id, title }.
 * Offered in BAM IQ as "Attach"; never attached on its own. Safe without a provider.
 */
export function useBamIQPage(item) {
  const ctx = useContext(Ctx);
  const setPage = ctx?.setPage;
  const on = !!ctx?.enabled;
  const type = item?.type || null;
  const id = item?.id ? String(item.id) : null;
  const title = item?.title || null;
  useEffect(() => {
    if (!on || !setPage || !type || !id) return;
    setPage({ type, id, title });
    return () => setPage(p => (p && p.type === type && p.id === id ? null : p));
  }, [on, setPage, type, id, title]);
}

/* ── the orb ───────────────────────────────────────────────────────────── */

/** Types each suggestion out, holds it, takes it back, and moves on. */
export function useTypewriter(list, active) {
  const [i, setI] = useState(0);
  const [n, setN] = useState(0);
  const [back, setBack] = useState(false);
  const still = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const text = list[i % list.length] || "";
  useEffect(() => {
    if (!active) return undefined;
    if (still) { const t = setTimeout(() => setI(x => x + 1), 3200); return () => clearTimeout(t); }
    let t;
    if (!back && n < text.length) t = setTimeout(() => setN(n + 1), 34 + Math.random() * 40);
    else if (!back) t = setTimeout(() => setBack(true), 1700);
    else if (n > 0) t = setTimeout(() => setN(n - 1), 16);
    else { setBack(false); setI(x => x + 1); }
    return () => clearTimeout(t);
  }, [active, still, n, back, text]);
  useEffect(() => { if (!active) { setN(0); setBack(false); } }, [active]);
  return { shown: still ? text : text.slice(0, n), full: text };
}

function OrbFace({ size = 60 }) {
  return (
    <span aria-hidden="true" style={{ width: size, height: size, flexShrink: 0, display: "grid", placeItems: "center", alignContent: "center", gap: 2 }}>
      <img src={BAM_LOGO_PNG} alt="" width={20} height={24} style={{ display: "block", filter: "drop-shadow(0 1px 4px rgba(226,221,159,.35))" }}/>
      <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 11, letterSpacing: ".1em", lineHeight: 1, color: GOLD }}>BAM IQ</span>
    </span>
  );
}

export function BamIQOrb({ C, isMobile, hidden = false }) {
  const ctx = useBamIQContext();
  const [q, setQ] = useState("");
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const expanded = !!ctx?.orbOpen;
  const setExpanded = ctx?.setOrbOpen;
  const tw = useTypewriter(ORB_SUGGESTIONS, expanded && !q);

  useEffect(() => { if (expanded) setTimeout(() => inputRef.current?.focus(), 60); }, [expanded]);
  useEffect(() => {
    if (!expanded) return undefined;
    const away = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target) && !q) setExpanded(false); };
    document.addEventListener("pointerdown", away);
    return () => document.removeEventListener("pointerdown", away);
  }, [expanded, q, setExpanded]);
  if (!ctx?.enabled || hidden || ctx.open) return null;
  const { iq } = ctx;
  const ask = (text) => {
    const t = String(text || "").trim();
    if (!t) return;
    setQ(""); setExpanded(false);
    ctx.setOpen(true);
    iq.send(t, { fresh: true });
  };
  const current = iq.threadId ? iq.threads.find(t => t.id === iq.threadId) : null;
  const width = isMobile ? "calc(100vw - 32px)" : 460;
  return (
    <div ref={wrapRef} className="bamiq-orb-wrap" data-bamiq-orb>
      {expanded && (current || ctx.pageThread) && (
        <div className="bamiq-rise" style={{ display: "flex", justifyContent: "flex-end", gap: 6, marginBottom: 8 }}>
          {[current && { label: "Continue", t: current }, ctx.pageThread && { label: "About this page", t: ctx.pageThread }].filter(Boolean).slice(0, 1).map(({ label, t }) => (
            <button key={t.id} type="button" onClick={() => { setExpanded(false); iq.openThread(t.id); ctx.setOpen(true); }}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, maxWidth: isMobile ? "calc(100vw - 32px)" : 360, padding: "6px 12px", borderRadius: 999,
                border: `1px solid ${C.border}`, background: C.bgCard, boxShadow: C.elevatedShadow, color: C.text, fontFamily: ff, fontSize: 12.5, cursor: "pointer" }}>
              <CornerDownLeft size={13} color={C.textMid}/><span style={{ color: C.textMid }}>{label}:</span>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.title || "Conversation"}</span>
            </button>
          ))}
        </div>
      )}
      <div className="bamiq-orb bamiq-ring" style={{ display: "flex", alignItems: "center", height: 60, width: expanded ? width : 60, maxWidth: "calc(100vw - 32px)",
        borderRadius: 30, overflow: "hidden", marginLeft: "auto",
        background: expanded ? "rgba(17,17,17,0.92)" : "radial-gradient(circle at 32% 26%, #2b2b2b 0%, #121212 58%, #070707 100%)",
        backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)" }}>
        {expanded ? (
          <form onSubmit={(e) => { e.preventDefault(); ask(q); }} style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 6, padding: "0 6px 0 20px" }}>
            <div style={{ position: "relative", flex: 1, minWidth: 0, height: 60, display: "flex", alignItems: "center" }}>
              <label htmlFor="bamiq-orb-input" style={{ position: "absolute", left: -9999 }}>Ask BAM IQ</label>
              <input id="bamiq-orb-input" ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} autoComplete="off" enterKeyHint="send"
                onKeyDown={(e) => {
                  if (e.key === "Escape") { e.stopPropagation(); setQ(""); setExpanded(false); }
                  else if ((e.key === "Tab" || e.key === "ArrowRight") && !q && tw.full) { e.preventDefault(); setQ(tw.full); }
                }}
                style={{ width: "100%", border: "none", outline: "none", background: "transparent", color: "#F5F5F5", fontFamily: ff, fontSize: 15, padding: 0 }}/>
              {!q && (
                <div aria-hidden="true" style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", gap: 8, pointerEvents: "none", overflow: "hidden" }}>
                  <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "rgba(245,245,245,.5)", fontFamily: ff, fontSize: 15 }}>
                    {tw.shown}<span className="bamiq-caret"/>
                  </span>
                  {!isMobile && <span style={{ marginLeft: "auto", flexShrink: 0, padding: "1px 6px", borderRadius: 5, border: "1px solid rgba(255,255,255,.14)", color: "rgba(245,245,245,.45)", fontSize: 10.5, fontFamily: ff }}>Tab</span>}
                </div>
              )}
            </div>
            <button type="submit" aria-label="Ask" disabled={!q.trim()}
              style={{ display: "grid", placeItems: "center", width: 36, height: 36, borderRadius: 999, border: "none", flexShrink: 0,
                background: q.trim() ? GOLD : "rgba(226,221,159,.14)", color: q.trim() ? "#111" : "rgba(226,221,159,.55)",
                cursor: q.trim() ? "pointer" : "default", transition: "background .15s ease, color .15s ease" }}>
              <ArrowUp size={18} strokeWidth={2.5}/>
            </button>
            <button type="button" aria-label="Open BAM IQ" title="Open BAM IQ" onClick={() => { setExpanded(false); ctx.setOpen(true); }}
              style={{ border: "none", background: "transparent", padding: 0, cursor: "pointer" }}>
              <OrbFace size={48}/>
            </button>
          </form>
        ) : (
          <button type="button" aria-label="Ask BAM IQ" aria-keyshortcuts="Meta+J Control+J" onClick={() => setExpanded(true)}
            style={{ border: "none", background: "transparent", padding: 0, cursor: "pointer" }}>
            <OrbFace/>
          </button>
        )}
      </div>
    </div>
  );
}

/** ⌘J / Ctrl+J: the orb's ask bar, or close the panel; on #/iq, the box to ask in. */
export function useBamIQShortcut({ onIqPage }) {
  const ctx = useBamIQContext();
  useEffect(() => {
    if (!ctx?.enabled) return undefined;
    const onKey = (e) => {
      if (!(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey || String(e.key).toLowerCase() !== "j") return;
      e.preventDefault();
      if (onIqPage) { document.getElementById("bamiq-input")?.focus(); return; }
      if (ctx.open) { ctx.setOpen(false); return; }
      ctx.setOrbOpen(o => !o);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ctx, onIqPage]);
}

/* ── the panel ─────────────────────────────────────────────────────────── */

export function BamIQPanel({ C, go, isMobile }) {
  const ctx = useBamIQContext();
  const [width, setWidth] = useState(() => Math.max(PANEL_MIN, Math.min(PANEL_MAX, Number(store.get(PANEL_W_KEY, 440)) || 440)));
  const [vw, setVw] = useState(() => (typeof window !== "undefined" ? window.innerWidth : 1280));
  const [view, setView] = useState("chat");   // chat | history | memory
  const [dragging, setDragging] = useState(false);
  const open = !!ctx?.enabled && !!ctx.open;
  const push = open && !isMobile && vw - width >= PUSH_MIN_PAGE;
  const close = () => ctx.setOpen(false);

  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  // The page makes room: the shell's padding follows --bamiq-push.
  useEffect(() => {
    const root = document.documentElement.style;
    root.setProperty("--bamiq-push", push ? `${width}px` : "0px");
    root.setProperty("--bamiq-push-t", dragging ? "0s" : ".28s");
    return () => root.setProperty("--bamiq-push", "0px");
  }, [push, width, dragging]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape" && !e.defaultPrevented) ctx.setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, ctx]);
  useEffect(() => { if (open) setView("chat"); }, [open]);

  if (!open) return null;
  const { iq } = ctx;
  const title = view === "history" ? "Conversations" : view === "memory" ? "What BAM IQ knows about you"
    : iq.threadId ? (iq.threads.find(t => t.id === iq.threadId)?.title || "Conversation") : "New conversation";

  const startDrag = (e) => {
    e.preventDefault();
    setDragging(true);
    const move = (ev) => setWidth(Math.round(Math.max(PANEL_MIN, Math.min(PANEL_MAX, window.innerWidth - 420, window.innerWidth - ev.clientX))));
    const up = () => {
      setDragging(false);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      setWidth(w => { store.set(PANEL_W_KEY, w); return w; });
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  return (
    <aside role="dialog" aria-label="BAM IQ" className="bamiq-panel" style={{
      position: "fixed", zIndex: 5200, top: 0, right: 0, bottom: 0, width: isMobile ? "100%" : width, maxWidth: "100%",
      background: C.bgCard, borderLeft: isMobile ? "none" : `1px solid ${C.border}`, boxShadow: push ? "none" : C.elevatedShadow,
      display: "flex", flexDirection: "column", paddingTop: "env(safe-area-inset-top, 0px)", paddingBottom: "env(safe-area-inset-bottom, 0px)",
      userSelect: dragging ? "none" : undefined }}>
      {!isMobile && (
        <div role="separator" aria-orientation="vertical" aria-label="Resize BAM IQ" tabIndex={0} className={`bamiq-resize${dragging ? " dragging" : ""}`}
          onPointerDown={startDrag}
          onKeyDown={(e) => {
            const step = e.shiftKey ? 60 : 20;
            if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
              e.preventDefault();
              setWidth(w => { const n = Math.max(PANEL_MIN, Math.min(PANEL_MAX, w + (e.key === "ArrowLeft" ? step : -step))); store.set(PANEL_W_KEY, n); return n; });
            }
          }}
          style={{ position: "absolute", left: -5, top: 0, bottom: 0, width: 10, cursor: "ew-resize", zIndex: 3, display: "flex", justifyContent: "center" }}>
          <span style={{ width: 2, height: "100%", background: GOLD, opacity: 0, transition: "opacity .15s ease" }}/>
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 12px 10px 16px", borderBottom: `1px solid ${C.border}` }}>
        <img src={BAM_LOGO_PNG} alt="" width={15} height={18} style={{ flexShrink: 0 }}/>
        <div style={{ flex: 1, minWidth: 0, marginLeft: 4, fontFamily: ff, fontWeight: 700, fontSize: 13.5, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title}</div>
        <IconBtn C={C} label="Conversations" active={view === "history"} onClick={() => setView(v => (v === "history" ? "chat" : "history"))}><History size={15}/></IconBtn>
        <IconBtn C={C} label="What BAM IQ knows about you" active={view === "memory"} onClick={() => setView(v => (v === "memory" ? "chat" : "memory"))}><UserRound size={15}/></IconBtn>
        <IconBtn C={C} label="New conversation" onClick={() => { iq.newThread(); setView("chat"); }}><Plus size={16}/></IconBtn>
        {!isMobile && <IconBtn C={C} label="Open the full page" onClick={() => { close(); go("iq"); }}><Maximize2 size={14}/></IconBtn>}
        <IconBtn C={C} label="Close" onClick={close}><X size={17}/></IconBtn>
      </div>
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        {view === "history" && <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 14 }}><ThreadList iq={iq} C={C} onPick={(id) => { iq.openThread(id); setView("chat"); }}/></div>}
        {view === "memory" && <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 16 }}><MemoryCard iq={iq} C={C}/></div>}
        {view === "chat" && (
          <BamIQConversation iq={iq} page={ctx.page} C={C} compact autoFocus={!isMobile} pageThread={ctx.pageThread}
            onShowMemory={() => setView("memory")}
            go={(to) => { if (isMobile) close(); go(to); }}/>
        )}
      </div>
    </aside>
  );
}

function IconBtn({ C, label, onClick, active, children }) {
  return (
    <button type="button" aria-label={label} title={label} onClick={onClick} aria-pressed={active ?? undefined}
      style={{ display: "grid", placeItems: "center", width: 30, height: 30, borderRadius: 8, border: "none",
        background: active ? `${GOLD}22` : "transparent", color: active ? C.text : C.textMid, cursor: "pointer" }}>
      {children}
    </button>
  );
}

/* ── the #/iq page ─────────────────────────────────────────────────────── */

export function BamIQWorkspace({ C, go, isMobile }) {
  const ctx = useBamIQContext();
  const [showList, setShowList] = useState(false);
  const [view, setView] = useState("chat");   // chat | cases (the founder's test cases)
  const memoryRef = useRef(null);
  if (!ctx?.enabled) return null;
  const { iq } = ctx;
  const showMemory = () => {
    if (isMobile) setShowList(true);
    setTimeout(() => memoryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };
  const list = (
    <div style={{ display: "flex", flexDirection: "column", gap: 18, padding: 16, minHeight: 0, overflowY: "auto", fontFamily: ff }}>
      {iq.founder && (
        <button type="button" data-cases-tab onClick={() => { setView(v => (v === "cases" ? "chat" : "cases")); setShowList(false); }}
          style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 12px", borderRadius: 10,
            border: `1px solid ${view === "cases" ? GOLD : C.border}`, background: view === "cases" ? `${GOLD}14` : "transparent", color: C.text, fontFamily: ff, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
          Test cases <span style={{ color: C.textMid, fontWeight: 600 }}>{iq.cases ? iq.cases.length : ""} · only you</span>
        </button>
      )}
      <button type="button" onClick={() => { iq.newThread(); setView("chat"); setShowList(false); }}
        style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 12px", borderRadius: 10, border: "none",
          background: GOLD, color: "#111", fontWeight: 800, fontSize: 13.5, fontFamily: ff, cursor: "pointer" }}><Plus size={15}/> New conversation</button>
      <ThreadList iq={iq} C={C} onPick={(id) => { iq.openThread(id); setView("chat"); setShowList(false); }}/>
      <div ref={memoryRef}><MemoryCard iq={iq} C={C}/></div>
    </div>
  );
  return (
    <div style={{ display: "flex", height: "100%", minHeight: 0, background: C.bg }}>
      {!isMobile && <div style={{ width: 290, flexShrink: 0, borderRight: `1px solid ${C.border}`, display: "flex", flexDirection: "column", minHeight: 0 }}>{list}</div>}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", minHeight: 0 }}>
        {isMobile && (
          <div style={{ padding: "8px 12px", borderBottom: `1px solid ${C.border}` }}>
            <button type="button" onClick={() => setShowList(s => !s)} style={{ border: `1px solid ${C.border}`, background: "transparent", color: C.text, borderRadius: 8, padding: "6px 10px", fontFamily: ff, fontSize: 13, cursor: "pointer" }}>
              {showList ? "Back to the conversation" : "Conversations and memory"}
            </button>
          </div>
        )}
        {isMobile && showList ? list : view === "cases" && iq.founder ? <BamIQCases iq={iq} C={C} isMobile={isMobile}/> : (
          <div style={{ flex: 1, minHeight: 0, maxWidth: 860, width: "100%", margin: "0 auto" }}>
            {/* A source opened from here keeps the conversation beside it, in the panel. */}
            <BamIQConversation iq={iq} page={ctx.page} C={C} autoFocus={!isMobile} onShowMemory={showMemory}
              go={(to) => { if (!isMobile) ctx.setOpen(true); go(to); }}/>
          </div>
        )}
      </div>
    </div>
  );
}
